import React, {useMemo} from 'react';
import { useTitle } from "fearless-common-ui";
import {Alert, Badge, Card} from "react-bootstrap";
import moment from "moment/moment";
import {concepts, Group, User} from "fearless-common";
import {standardDateFormat} from "../../../utils/momentHelpers";
import SubscriptionCard from "../../../components/subscriptions/SubscriptionCard";
import Photo51SubscriptionSwitcher from "../../../components/subscriptions/Photo51SubscriptionSwitcher";
import Header from "../../../components/Header";
import {isDev, isInternal} from "../../../utils/env";
import {buildClientReferenceId, stripePriceTables} from "../../../utils/stripe";
import StripePricingTable, {PRICING_TABLE_PRODUCT} from "../../../components/StripePricingTable";

/**
 * An overview of the logged-in users subscriptions
 */
export default function MySubscriptionsOverview({ user, reloadUser }: {
    /** The logged-in user */
    user: User & { groups: Group[] };
    reloadUser: () => void;
}) {
    useTitle('My subscription');
    const activePhoto51SubscriptionJsx = useMemo(() => {
        const { photo51 } = user?.subscription_per_product || {};

        if (photo51?.via === 'individual' && photo51.subscription) {
            return <SubscriptionCard subscription={photo51.subscription} reloadSubscriptions={reloadUser} />;
        }
        if (photo51?.via === 'group' && photo51.subscription) {
            return (
              <SubscriptionCard
                subscription={photo51.subscription}
                group={photo51.group}
                reloadSubscriptions={reloadUser}
              />
            );
        }
        if (photo51?.via === 'rule' && photo51.rule) {
            return <SubscriptionCard.Rule rule={photo51.rule} user={user} subscribedUntil={photo51.subscribed_until} />;
        }
        return null
    }, [user, reloadUser]);

    const activeElnSubscriptionJsx = useMemo(() => {
        const { eln } = user?.subscription_per_product || {};

        if (eln?.via === 'individual' && eln.subscription) return <SubscriptionCard subscription={eln.subscription} reloadSubscriptions={reloadUser} />;
        if (eln?.via === 'group' && eln.subscription) return <SubscriptionCard subscription={eln.subscription} group={eln.group} reloadSubscriptions={reloadUser} />;
        if (eln?.via === 'rule' && eln.rule) return <SubscriptionCard.Rule rule={eln.rule} user={user} subscribedUntil={eln.subscribed_until} />;
        return <i>No {concepts.Subscription.toLowerCase()} for ELN</i>;
    }, [user]);

    const activeLimsSubscriptionJsx = useMemo(() => {
        const { lims } = user?.subscription_per_product || {};

        if (lims?.via === 'individual' && lims.subscription) return <SubscriptionCard subscription={lims.subscription} reloadSubscriptions={reloadUser} />;
        if (lims?.via === 'group' && lims.subscription) return <SubscriptionCard subscription={lims.subscription} group={lims.group} reloadSubscriptions={reloadUser} />;
        if (lims?.via === 'rule' && lims.rule) return <SubscriptionCard.Rule rule={lims.rule} user={user} subscribedUntil={lims.subscribed_until} />;
        return <i>No {concepts.Subscription.toLowerCase()} for Lims</i>;
    }, [user]);

    return (
        <>
            {user.on_trial && (
                <Alert variant="info">
                    Your free trial period will end&nbsp;
                    {moment(user.trial_ends_at).format(standardDateFormat)}
                    . Please select a subscription to keep all your features.
                </Alert>
            )}
            {(!user.is_subscribed || user.on_trial) && (
              <>
                  <h3 className="mb-3 mt-5 fw-normal">Please select a {concepts.Subscription}</h3>
                  {user && (
                    <StripePricingTable
                      tableId={stripePriceTables.INDIVIDUAL}
                      clientReferenceId={buildClientReferenceId('user', user.id)}
                      postPrices={[PRICING_TABLE_PRODUCT.INSTITUTE]}
                    />
                  )}
              </>
            )}
            {activePhoto51SubscriptionJsx}
            {user.is_subscribed && (
              <Card className="border-0 d-inline-block mt-5">
                <Card.Body className="bg-neutral-200 p-3 rounded-3">
                    <Photo51SubscriptionSwitcher onSuccess={() => reloadUser()} user={user} />
                </Card.Body>
              </Card>
            )}
            {(isInternal() || isDev()) && (
                <>
                    <div className="py-vh15" />
                    <Header component="h4">
                        Eln&nbsp;
                        <Badge>Internal only</Badge>
                    </Header>
                    {activeElnSubscriptionJsx}
                    <hr className="my-5" />
                    <Header component="h4">
                        Lims&nbsp;
                        <Badge>Internal only</Badge>
                    </Header>
                    {activeLimsSubscriptionJsx}
                </>
            )}
        </>
    );
}
